import axios from "axios";
import { message } from "antd";
import { createBrowserHistory } from "history";
import { createStore } from "jotai";
import { userAtom } from "@/stores/userAtom";
import { getToken, removeToken } from "@/utils/auth";
import { useCompanyStore } from "@/stores/useCompanyStore";
// 全局实例
const jotaiStore = createStore();
export const history = createBrowserHistory();
export const isRelogin = { show: false };
// 错误码映射
const ERROR_CODE_MAP = {
    200: "操作成功",
    401: "登录状态已过期，请重新登录",
    403: "无权限访问",
    404: "接口不存在",
    500: "服务器内部错误",
    default: "请求异常，请稍后重试",
};
// 创建实例
const apiClient = axios.create({
    baseURL: process.env.REACT_APP_BASE_API || "/api",
    timeout: 20000,
    headers: {
        "Content-Type": "application/json;charset=utf-8",
    },
});
// 请求拦截器
apiClient.interceptors.request.use((config) => {
    config.headers = config.headers || {};
    // 自动带token（登录接口跳过）
    const token = getToken();
    const isLoginUrl = config.url?.includes("/auth/login");
    if (token && !isLoginUrl) {
        config.headers.Authorization = `Bearer ${token}`;
        const { company } = useCompanyStore.getState();
        if (company?.companyCode && company.companyCode !== "0") {
            config.headers["Business-Entity-Code"] = company.companyCode;
            config.headers["CompanyCode"] = company.companyCode;
        }
        else {
            config.headers["Business-Entity-Code"] = company.companyCode;
            config.headers["CompanyCode"] = company.companyCode;
        }
        if (company?.companyName && company.companyCode !== "0") {
            config.headers["Business-Entity-Name"] = encodeURIComponent(company.companyName);
            config.headers["CompanyName"] = encodeURIComponent(company.companyName);
        }
        else {
            config.headers["Business-Entity-Name"] = encodeURIComponent(company.companyName);
            config.headers["CompanyName"] = encodeURIComponent(company.companyName);
        }
    }
    if (config.method === "post" && !isLoginUrl) {
        const requestKey = `${config.url}-${JSON.stringify(config.data || {})}`;
        const lastTime = sessionStorage.getItem(`repeat_${requestKey}`);
        const now = Date.now();
        if (lastTime && now - Number(lastTime) < 1000) {
            message.warning("请勿重复提交");
            throw new Error("重复提交拦截");
        }
        sessionStorage.setItem(`repeat_${requestKey}`, now.toString());
        setTimeout(() => sessionStorage.removeItem(`repeat_${requestKey}`), 1000);
    }
    // GET防缓存
    if (config.method === "get") {
        const t = Date.now();
        config.url = `${config.url || ""}${config.url?.includes("?") ? "&" : "?"}t=${t}`;
    }
    return config;
}, (error) => {
    message.error("请求初始化失败");
    return Promise.reject(error);
});
// 响应拦截器
apiClient.interceptors.response.use((response) => {
    const { code, msg } = response.data;
    const errorMsg = ERROR_CODE_MAP[code] || msg || ERROR_CODE_MAP.default;
    // 401处理 - 直接跳转而不依赖React Router
    if (code === 401 && !isRelogin.show) {
        removeToken();
        jotaiStore.set(userAtom, null);
        navigateToLogin();
        // 返回一个被拒绝的Promise而不是空Promise
        // return Promise.reject(new Error('未授权，请重新登录'));
    }
    if (code !== 200) {
        message.error(errorMsg);
        throw new Error(errorMsg);
    }
    return response;
}, (error) => {
    let msg = error.message || "请求失败";
    if (msg.includes("Network Error"))
        msg = "后端连接异常";
    if (msg.includes("timeout"))
        msg = "请求超时";
    // HTTP状态码错误处理
    if (error.response?.status === 401) {
        removeToken();
        jotaiStore.set(userAtom, null);
        if (!isRelogin.show) {
            isRelogin.show = true;
            const redirectUrl = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.href = `/login?redirect=${redirectUrl}`;
        }
        // 返回一个被拒绝的Promise而不是空Promise
        return Promise.reject(new Error("未授权，请重新登录"));
    }
    else if (error.response?.status) {
        msg = `接口${error.response.status}异常`;
    }
    message.error({ content: msg, duration: 5 });
    return Promise.reject(error);
});
// 核心请求函数（只接收url和data）
const createRequest = (method) => async (url, data) => {
    try {
        const config = {
            method,
            url,
            headers: {},
            ...(method === "get" ? { params: data } : { data }),
        };
        const response = await apiClient.request(config);
        return response.data;
    }
    catch (reason) {
        throw axios.isAxiosError(reason)
            ? {
                code: reason.response?.status || 500,
                message: reason.message || "请求失败",
                details: reason.response?.data,
            }
            : {
                code: 500,
                message: reason instanceof Error ? reason.message : "未知错误",
            };
    }
};
// 只暴露get和post，且仅接收url和data两个参数
export const ApiService = {
    // GET: url + params（可选）
    get: (url, params) => createRequest("get")(url, params),
    // POST: url + data（可选）
    post: (url, data) => createRequest("post")(url, data),
};
export const navigateToLogin = (redirectPath) => {
    if (window.location.pathname === "/login")
        return;
    const redirect = redirectPath || window.location.pathname + window.location.search;
    window.location.href = `/login?redirect=${encodeURIComponent(redirect)}`;
};
export default ApiService;
